import { useEffect, useState } from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

/**
 * SimilarJobsStrip — "Focus your day" list inside the JobDetailSheet.
 *
 * Fetches other tracked vacancies with a cached AI match-score close to
 * the currently-open one (±10 by default) so users can jump between
 * similarly-ranked opportunities without hopping back to the overview.
 *
 * The card is hidden entirely when:
 *   • the current job has no `match_insight.score` yet, OR
 *   • there are no other jobs in the score window.
 *
 * This is deliberately a pure GET — no LLM calls happen here; the strip
 * only ever surfaces already-cached scores.
 */
const SCORE_STYLES = (s) =>
  s >= 80 ? "bg-emerald-700 text-[#F9F8F6]" :
  s >= 60 ? "bg-lime-700 text-[#F9F8F6]" :
  s >= 40 ? "bg-amber-600 text-[#F9F8F6]" :
            "bg-red-600 text-[#F9F8F6]";

export default function SimilarJobsStrip({ jobId, onOpen }) {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!jobId) return;
    setLoading(true);
    api.get(`/jobs/${jobId}/similar?window=15&limit=5`)
      .then((r) => {
        if (!alive) return;
        setItems(r.data?.items || []);
        setCurrent(r.data?.current_score ?? null);
      })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [jobId]);

  if (loading) return null;
  if (current === null) return null;
  if (!items.length) return null;

  return (
    <section data-testid="similar-jobs-strip" className="mt-6">
      <p className="ed-eyebrow mb-4 flex items-center gap-2">
        <Sparkles size={12} strokeWidth={1.75} />
        {t("similarStripEyebrow")}
      </p>
      <div className="ed-card p-4">
        <p className="text-sm text-neutral-600 mb-4 leading-relaxed">
          {t("similarStripHint").replace("{s}", String(current))}
        </p>
        <ul className="divide-y divide-neutral-200" data-testid="similar-jobs-list">
          {items.map((j) => (
            <li key={j.id}>
              <button
                type="button"
                data-testid={`similar-jobs-item-${j.id}`}
                onClick={() => onOpen?.(j.id)}
                className="w-full flex items-center gap-3 py-3 text-left hover:bg-neutral-50 transition-colors group"
              >
                <span
                  className={`inline-block font-mono text-[0.65rem] font-bold px-2 py-0.5 shrink-0 ${SCORE_STYLES(j.score)}`}
                >
                  {j.score}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="font-serif text-base leading-tight block truncate">
                    {j.job_title}
                  </span>
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
                    {j.company}{j.location ? ` · ${j.location}` : ""}
                  </span>
                </span>
                <ArrowRight
                  size={14}
                  strokeWidth={1.5}
                  className="text-neutral-400 group-hover:text-neutral-900 shrink-0 transition-colors"
                />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
